import React from "react";
import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSDropFileInput,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { useQuerySharedFileSettings } from "../../../../../query/sharedFile/query/useQuerySharedFileSettings";
import { WSQueries } from "../../../../../query/WSQuery";
import { useMutationUpdateSharedFile } from "../../../../../query/sharedFile/mutations/useMutationUpdateSharedFile";
import { useMutationMemberPrivateFileCreate } from "../../../../../query/files/mutations";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { mimeTypesToGroups } from "../../../../RequirementDefinitions/modules/SharedFile/createRequirementDefinition/utils";
import { getPayerEngagements } from "../../../../../services/payerEngagements";
import { MAX_COI_FILE_UPLOAD_ATTEMPTS } from "../configs/utils";
import { RequirementInstructionsView } from "../../../components/RequirementInstructionsView/RequirementInstructionsView";

type Props = {
  onSuccess: (fileId: string) => void;
  sharedFileRequestId: string;
  sharedFileRequestSettingsId: string;
  onClose: (result: boolean) => void;
  payerId: string | undefined;
  payeeId: string | undefined;
  isCOI?: boolean;
  attempts: number;
};

const UploadFileModal: React.FC<Props> = ({
  sharedFileRequestId,
  sharedFileRequestSettingsId,
  onSuccess,
  onClose,
  payerId,
  payeeId,
  isCOI,
  attempts
}) => {
  const [file, setFile] = React.useState<File | null>(null);
  const { openSnackbar } = useWSSnackbar();

  const [uploadFile, uploadFileMeta] = useMutationMemberPrivateFileCreate({
    hidden: true,
    viewerIds: [payeeId, payerId].filter(Boolean) as string[]
  });

  const querySharedFileSettings = useQuerySharedFileSettings(
    sharedFileRequestSettingsId
  );

  const [updateSharedFile, updateSharedFileMeta] =
    useMutationUpdateSharedFile();

  return (
    <WSQueries queries={{ querySharedFileSettings }}>
      {({ querySharedFileSettingsData }) => {
        const formatsList = mimeTypesToGroups(
          querySharedFileSettingsData?.mimeTypes || []
        ).join(", ");
        return (
          <WSList gap="L">
            {isCOI ? (
              <>
                <WSText.ParagraphSm color="gray500">
                  Please check that your file is up to date and accurate.
                </WSText.ParagraphSm>
                <WSInfoBox>
                  <WSText.ParagraphSm weight="medium">
                    Important:
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm>
                    When the file is uploaded, it will be validated according to
                    your client’s policies.
                  </WSText.ParagraphSm>
                  <ul>
                    <li>The uploaded file is visible to the client.</li>
                    <li>
                      The upload and validation process cannot be interrupted
                      once started.
                    </li>
                    <li>
                      If all validation passes, the requirement is considered
                      complete.
                    </li>
                    <li>
                      If any validation fails, the requirement is incomplete.
                      You have the option to retry with a new file (up to{" "}
                      {MAX_COI_FILE_UPLOAD_ATTEMPTS} times).
                    </li>
                  </ul>
                </WSInfoBox>

                <WSPanel>
                  <WSFlexBox.CenterY justify="space-between">
                    <WSText.ParagraphSm>
                      Upload attempts left:
                    </WSText.ParagraphSm>
                    <WSText.ParagraphSm>
                      {MAX_COI_FILE_UPLOAD_ATTEMPTS - attempts}
                    </WSText.ParagraphSm>
                  </WSFlexBox.CenterY>
                </WSPanel>
              </>
            ) : querySharedFileSettingsData.instructions ? (
              <WSInfoBox>
                <RequirementInstructionsView
                  instructions={querySharedFileSettingsData.instructions}
                />
              </WSInfoBox>
            ) : null}

            {file ? (
              <WSPanel p="XL">
                <WSFlexBox.CenterY wrap="nowrap">
                  <WSText.Paragraph singleLine>{file.name}</WSText.Paragraph>
                  <WSFlexBox>
                    <WSIcon
                      block
                      color="gray500"
                      size="M"
                      mx="M"
                      name="exit"
                      onClick={() => {
                        setFile(null);
                      }}
                    />
                  </WSFlexBox>
                </WSFlexBox.CenterY>
              </WSPanel>
            ) : (
              <WSElement>
                <WSText.ParagraphSm color="gray500" mb="S">
                  Only {formatsList} files are accepted.
                </WSText.ParagraphSm>
                <WSDropFileInput
                  name="dropFileInput"
                  buttonText="Browse file"
                  buttonProps={{
                    type: "button",
                    icon: "upload"
                  }}
                  maxSize={1000000 * 100}
                  accept={querySharedFileSettingsData.mimeTypes}
                  onDropAccepted={accepted => {
                    setFile(accepted[0]);
                  }}
                />
              </WSElement>
            )}

            <WSErrorMessage
              error={updateSharedFileMeta.error || uploadFileMeta.error}
              contextKey="Other"
            />

            <WSActions
              alignment="fill"
              orientation="horizontal"
              buttons={[
                {
                  kind: "Tertiary",
                  label: "Cancel",
                  onClick: () => {
                    onClose(false);
                  }
                },
                {
                  kind: "Primary",
                  label: "Continue with upload",
                  disabled: !file,
                  onAsyncClick: async () => {
                    const data = new FormData();
                    data.append("file", file!);

                    const uploadFileResponse = await uploadFile(data, {
                      onError: error => {
                        openSnackbar({
                          message: "Failed to upload file",
                          type: "error"
                        });
                      },
                      throwOnError: true
                    });

                    const fileId = uploadFileResponse?.[0].fileId;

                    if (fileId) {
                      const updatedSharedFile = await updateSharedFile(
                        {
                          sharedFileId: sharedFileRequestId,
                          fileId
                        },
                        {
                          onError: error => {
                            openSnackbar({
                              message: "Failed to update shared file",
                              type: "error"
                            });
                          },
                          throwOnError: true
                        }
                      );

                      if (updatedSharedFile) {
                        if (payerId) {
                          await getPayerEngagements(payerId);
                        }

                        openSnackbar({
                          message: "File uploaded successfully",
                          type: "success"
                        });

                        onSuccess(sharedFileRequestId);
                        onClose(true);

                        return;
                      }
                    }
                  }
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSQueries>
  );
};

export function useUploadFileModal() {
  return useWSModal(UploadFileModal, {
    title: "Upload file",
    size: "M"
  });
}
